import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import {
    RUM_SESSION_HEADER,
    readGroundcoverRumSessionId,
} from '@/lib/groundcoverRumSession';
import { getCookie } from '@/lib/utils';
import { apiLocale, withApiLocale } from '@/lib/apiFetch';
import { apiUrl } from '@/lib/appOrigin';

/**
 * Late-bound invalidate action for whichever api instance is running (user app or admin portal).
 * Dispatching it after an unrecoverable 401 re-renders the user query's subscribers; with the
 * sessioncache cookie cleared, `user` goes undefined and the app's route guard sends the visitor
 * to /login with return-page preservation. Registered by the api module that owns the store.
 */
let invalidateUserTagAction: () => unknown = () => null;

export const setInvalidateUserTagAction = (action: () => unknown): void => {
    invalidateUserTagAction = action;
};

/** Expire the sessioncache cookie: the server session is dead, so the client hint must not outlive it. */
function clearSessionCacheCookie(): void {
    if (typeof document === 'undefined') return;
    document.cookie = 'sessioncache=; Max-Age=0; path=/; SameSite=Lax';
}

/**
 * On 401 from any request (except session/refresh), try POST session/refresh once — single-flight,
 * so N concurrent 401s share one refresh call (parallel refreshes can trip Auth0's rotating-token
 * reuse detection and revoke the whole session) — and retry on success.
 * When the refresh itself fails AND a sessioncache cookie is present (we believed we had a
 * session), the session is unrecoverable: clear the cookie and flip the app's auth state so the
 * route guard redirects to /login with return-page preservation. Anonymous visitors have no
 * sessioncache cookie and are never touched — public pages routinely receive 401s (e.g. polling
 * endpoints). Primary session health is still the poll (useLogoutAfterMaxPolls).
 */
export function baseQueryWithRefresh(
    baseUrl: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta> {
    // In the browser this resolves at import, exactly as it always has. On the server it cannot:
    // the api instance is constructed while this module is imported, which happens before the
    // render has said where the api lives, so the server resolves on first use instead.
    const clientBaseUrl = typeof window === 'undefined'
        ? null
        : new URL(baseUrl, window.location.origin).href;

    let resolved: ReturnType<typeof fetchBaseQuery> | null = null;
    const baseQuery: ReturnType<typeof fetchBaseQuery> = (args, api, extraOptions) => {
        resolved ??= fetchBaseQuery({
            baseUrl: clientBaseUrl ?? apiUrl(baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`),
            prepareHeaders: (headers) => {
                const rumSessionId = readGroundcoverRumSessionId();
                if (rumSessionId) {
                    headers.set(RUM_SESSION_HEADER, rumSessionId);
                }
                // Phase 0.5 of the Spanish rollout. Read per request rather than captured once, so a
                // language switch applies to in-flight work without rebuilding the base query.
                // NOTE: headers are not part of RTK Query's cache key, so LanguageSwitcher must also
                // dispatch `api.util.resetApiState()` or cached responses keep the old language.
                // On the server there is no stored choice, so a render always asks in English.
                // The browser's stored choice in the SPA; the URL's language on the server (see apiFetch).
                headers.set('Accept-Language', apiLocale());
                return headers;
            },
        });
        // The language also goes in the URL (see withApiLocale): Front Door drops the header on caching routes.
        const localized = typeof args === 'string' ? withApiLocale(args) : { ...args, url: withApiLocale(args.url) };
        return resolved(localized, api, extraOptions);
    };
    let refreshInFlight: ReturnType<typeof baseQuery> | null = null;
    return async (args, api, extraOptions) => {
        const result = await baseQuery(args, api, extraOptions);
        const requestUrl = typeof args === 'string' ? args : (args as FetchArgs).url;
        const isRefreshEndpoint = requestUrl != null && String(requestUrl).includes('session/refresh');
        if (result.error?.status === 401 && !isRefreshEndpoint) {
            refreshInFlight ??= Promise.resolve(
                baseQuery({ url: 'session/refresh', method: 'POST' }, api, extraOptions),
            ).finally(() => {
                refreshInFlight = null;
            });
            const refreshResult = await refreshInFlight;
            if (refreshResult.data) {
                return baseQuery(args, api, extraOptions);
            }
            if (getCookie('sessioncache') !== null) {
                clearSessionCacheCookie();
                api.dispatch(invalidateUserTagAction() as never);
            }
        }
        return result;
    };
}
